import { format, parseISO } from "date-fns";
import Decimal from "decimal.js";

class ConstantFunctions {
  public toSentenceCase(str: string) {
    if (!str) return "";
    str = str.trim().toLowerCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  public dateFormatInDDMMM(dateString: string | null = ""): string {
    if (!dateString) {
      return ""; // Handle null or empty string
    } else {
      const date = parseISO(dateString);
      return format(date, "dd MMM");
    }
  }

  public dateFormatInDDMMYY(dateString: string | null = ""): string {
    if (!dateString) {
      return "";
    } else {
      const date = parseISO(dateString);
      return format(date, "dd/MM/yyyy");
    }
  }

  public formatDate(inputDate: string): string {
    const date = new Date(inputDate);
    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  }

  public formatDateTime(inputDate: string): string {
    const date = new Date(inputDate);
    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true, // Use true for 12-hour format, or false for 24-hour format
    };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  }

  public timeFormatInHHMMSS(dateString: string | null = ""): string {
    if (!dateString) {
      return ""; // Handle null or empty string
    }

    const date = parseISO(dateString);
    return format(date, "HH:mm:ss");
  }

  public getHashAddressText(address: string | null = ""): string {
    if (!address) {
      return ""; // Handle null or empty string
    } else {
      const addressLength = address.length;
      return (
        address.substring(0, 4) +
        "..." +
        address.substring(addressLength - 4, addressLength)
      );
    }
  }

  public formatAmount(amount: string | number): string {
    return new Decimal(amount || 0)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toNumber()
      .toFixed(2);
  }

  public getTimeDifferenceString = (
    createdAt: string,
    updatedAt: string,
  ): string => {
    // Parse the ISO date strings
    const createdDate = new Date(createdAt);
    const updatedDate = new Date(updatedAt);

    // Get the difference in milliseconds
    const differenceInMs = updatedDate.getTime() - createdDate.getTime();

    // Convert milliseconds to seconds, minutes, hours
    const differenceInSeconds = Math.floor(differenceInMs / 1000);
    const differenceInMinutes = Math.floor(differenceInSeconds / 60);
    const differenceInHours = Math.floor(differenceInMinutes / 60);

    // Return appropriate string based on the time difference
    if (differenceInSeconds < 60) {
      return `${differenceInSeconds} Sec`;
    } else if (differenceInMinutes < 60) {
      return `${differenceInMinutes} Min${differenceInMinutes > 1 ? "s" : ""}`;
    } else {
      return `${differenceInHours} Hour${differenceInHours > 1 ? "s" : ""}`;
    }
  };

  public getBrandName(): string {
    const hostname = window.location.hostname;
    if (hostname === "insights.paysonic.network") {
      return "Paysonic";
    }
    return "Tylt"; // Default brand name
  }

  public isPaysonic(): boolean {
    const hostname = window.location.hostname;
    if (hostname === "insights.paysonic.network") {
      return true;
    }
    return false;
  }
}

export default ConstantFunctions;
